import type { IsoDate, Transaction } from './model';
import type { BillRow } from './recurring';
import { spendingOf } from './spending';
import { shiftMonth, type MonthKey } from './analytics';

/**
 * Spending pace (V1.5): where this month is heading. Pure — the screens
 * pass entries and bill rows, nothing here reads a database.
 *
 * The month end is projected in three parts, each with its own honest rule:
 *  1. what was spent so far (every entry up to today, the spending rule
 *     applied: refunds subtract, transfers and tracked card bills are 0);
 *  2. the bills still due this month, at the amount one tap would record
 *     (a fixed bill's amount, a variable bill's last amount);
 *  3. everyday spending for the days left. Bill payments are left out of
 *     the rate (rent on the 1st is not a daily habit). The rate so far this
 *     month is blended with the everyday rate of the usual month (the
 *     previous three): early in a month the usual month leads, by the end
 *     this month's own rate does. Weight = share of the month gone.
 *
 * With no earlier month to lean on and less than a week of this one, there
 * is no projection yet: three days of spending times ten is a guess.
 */
export interface PaceInput {
  today: IsoDate;
  /**
   * Live entries from the three months before today's month through today
   * (later dates are ignored). Linked bill payments carry their
   * recurringTemplateId.
   */
  entries: readonly Transaction[];
  /** billRows(…, today) of the budget (or the part a filter keeps). */
  bills: readonly BillRow[];
  /** The budget's monthly target, if one is set. */
  targetMinor: number | null;
  /** Date of the budget's first entry: months (and days) before it are not history. */
  firstDate: IsoDate | null;
}

export type TargetStatus = 'under' | 'over' | 'already';

export interface Pace {
  month: MonthKey;
  /** Today's day of the month (1-based). */
  day: number;
  daysInMonth: number;
  /** Days after today. */
  daysLeft: number;
  /** Spending so far this month (may be negative with refunds only). */
  spent: number;
  /** Bills still to record this month, at their expected amount. */
  billsDue: number;
  billsDueCount: number;
  /** Open bills this month with no amount to expect yet (a variable bill never recorded). */
  billsUnknownCount: number;
  /** Everyday (non-bill) spending so far, and what the days left are expected to add. */
  everydaySoFar: number;
  everydayRest: number;
  /** Month-end projection; null while it is too early to tell. */
  projected: number | null;
  /** The usual month: mean total of the (up to) three months before, and how many there were. */
  usual: { value: number; months: number } | null;
  target: null | {
    value: number;
    status: TargetStatus;
    /** target − projection (negative = over at this pace). */
    margin: number | null;
    /** Left for everyday spending after the bills still due, and per day including today (null when nothing is left). */
    left: number;
    perDay: number | null;
  };
  /** Projection against the usual month (ratio null when the usual month is 0). */
  vsUsual: { delta: number; ratio: number | null } | null;
}

const MIN_DAYS_WITHOUT_HISTORY = 7;

const daysIn = (month: MonthKey) => new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).getUTCDate();

export function spendingPace({ today, entries, bills, targetMinor, firstDate }: PaceInput): Pace {
  const month = today.slice(0, 7);
  const day = Number(today.slice(8, 10));
  const daysInMonth = daysIn(month);
  const daysLeft = daysInMonth - day;
  const history = [1, 2, 3].map((n) => shiftMonth(month, -n)).filter((m) => firstDate !== null && m >= firstDate.slice(0, 7));

  let spent = 0;
  let everydaySoFar = 0;
  const monthTotal = new Map<MonthKey, number>();
  const monthEveryday = new Map<MonthKey, number>();
  for (const e of entries) {
    if (e.date > today) continue;
    const v = spendingOf(e);
    if (v === 0) continue;
    const m = e.date.slice(0, 7);
    const everyday = !e.recurringTemplateId;
    if (m === month) {
      spent += v;
      if (everyday) everydaySoFar += v;
    } else if (history.includes(m)) {
      monthTotal.set(m, (monthTotal.get(m) ?? 0) + v);
      if (everyday) monthEveryday.set(m, (monthEveryday.get(m) ?? 0) + v);
    }
  }

  // Bills still open whose due day (or, without one, whose period end) falls in this month.
  let billsDue = 0;
  let billsDueCount = 0;
  let billsUnknownCount = 0;
  for (const row of bills) {
    if (row.status === 'recorded') continue;
    if ((row.dueDate ?? row.period.end).slice(0, 7) !== month) continue;
    if (row.suggestedMinor === null) {
      billsUnknownCount++;
      continue;
    }
    const v = spendingOf({ type: row.template.type, countsAsSpending: row.template.countsAsSpending, amountMinor: row.suggestedMinor });
    if (v === 0) continue;
    billsDue += v;
    billsDueCount++;
  }

  // Days each history month really had: the first month counts from the budget's first day.
  const firstDay = (m: MonthKey) => (firstDate && firstDate.slice(0, 7) === m ? Number(firstDate.slice(8, 10)) : 1);
  const historyDays = history.reduce((s, m) => s + daysIn(m) - firstDay(m) + 1, 0);
  const historyEveryday = history.reduce((s, m) => s + (monthEveryday.get(m) ?? 0), 0);
  const elapsed = day - (firstDate && firstDate.slice(0, 7) === month ? firstDay(month) - 1 : 0);

  const currentRate = Math.max(0, everydaySoFar) / Math.max(1, elapsed);
  const usualRate = historyDays > 0 ? Math.max(0, historyEveryday) / historyDays : null;
  const weight = day / daysInMonth;
  const rate = usualRate === null ? currentRate : weight * currentRate + (1 - weight) * usualRate;
  const tooEarly = usualRate === null && elapsed < MIN_DAYS_WITHOUT_HISTORY;
  const everydayRest = tooEarly ? 0 : Math.round(rate * daysLeft);
  const projected = tooEarly ? null : Math.max(0, spent + billsDue + everydayRest);

  const usual =
    history.length > 0
      ? {
          value: Math.max(0, Math.round(history.reduce((s, m) => s + (monthTotal.get(m) ?? 0), 0) / history.length)),
          months: history.length,
        }
      : null;

  let target: Pace['target'] = null;
  if (targetMinor !== null && targetMinor > 0) {
    const left = targetMinor - Math.max(0, spent) - billsDue;
    const status: TargetStatus = spent > targetMinor ? 'already' : projected !== null && projected > targetMinor ? 'over' : 'under';
    target = {
      value: targetMinor,
      status,
      margin: projected === null ? null : targetMinor - projected,
      left,
      perDay: left > 0 ? Math.floor(left / (daysLeft + 1)) : null,
    };
  }

  const vsUsual =
    projected !== null && usual !== null
      ? {
          delta: projected - usual.value,
          ratio: usual.value > 0 ? (projected - usual.value) / usual.value : null,
        }
      : null;

  return {
    month,
    day,
    daysInMonth,
    daysLeft,
    spent,
    billsDue,
    billsDueCount,
    billsUnknownCount,
    everydaySoFar,
    everydayRest,
    projected,
    usual,
    target,
    vsUsual,
  };
}

/** How the projection reads against the usual month: within ±5 % is "about the same". */
export function usualVerdict(vsUsual: Pace['vsUsual']): 'more' | 'less' | 'same' | null {
  if (!vsUsual || vsUsual.ratio === null) return null;
  if (Math.abs(vsUsual.ratio) < 0.05) return 'same';
  return vsUsual.ratio > 0 ? 'more' : 'less';
}

/**
 * The projection as a line on the 1–31 day axis: nothing before today,
 * today's running total, then straight on to the projected month end.
 */
export function projectionLine(pace: Pace, spentToday: number): (number | null)[] {
  return Array.from({ length: 31 }, (_, i) => {
    const d = i + 1;
    if (pace.projected === null || d < pace.day || d > pace.daysInMonth) return null;
    if (pace.daysLeft === 0) return d === pace.day ? spentToday : null;
    return Math.round(spentToday + ((pace.projected - spentToday) * (d - pace.day)) / pace.daysLeft);
  });
}
